<template>
  <div class="template-container">
    <v-row no-gutters>
      <!-- Main Content -->
      <v-col>
        <v-card flat class="d-flex flex-column" style="height: 90vh">
          <!-- Header -->
          <v-card-title
            class="py-4 px-4 d-flex justify-space-between border-b border-gray-200 top-header flex-shrink-0"
          >
            <h3 class="form-title">
              {{ editingTemplate ? 'Edit Template' : 'Create New Template' }}
            </h3>
            <v-icon @click="closeDialog">mdi-close</v-icon>
          </v-card-title>

          <!-- Scrollable Content Area -->
          <v-card-text class="overflow-y-auto flex-grow-1 p-4">
            <!-- Template Info -->
            <div class="mb-4 pt-2">
              <h3 class="text-md font-medium mb-2">Template Information</h3>
              <v-row>
                <v-col cols="12" md="4">
                  <v-text-field
                    v-model="templateName"
                    label="Template Name*"
                    hint="Only lowercase letters, numbers and underscores are allowed. Spaces will be replaced with underscores."
                    persistent-hint
                    dense
                    outlined
                    placeholder="e.g. welcome_message"
                    :error-messages="errors.templateName"
                    @input="formatTemplateName"
                    :disabled="!!editingTemplate"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" md="4">
                  <v-select
                    v-model="category"
                    :items="categories"
                    label="Category"
                    dense
                    outlined
                  ></v-select>
                </v-col>
                <v-col cols="12" md="4">
                  <v-autocomplete
                    :items="notificationTriggerOptions"
                    v-model="selectedEventType"
                    label="Select Event"
                    dense
                    outlined
                    class="w-full"
                    :clearable="true"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </div>

            <!-- Unified Template Toggle -->
            <div class="mb-4">
              <v-row align="center">
                <v-col cols="auto">
                  <v-checkbox
                    v-model="unifiedTemplate"
                    label="Create unified template for all channels"
                    dense
                    class="mt-0 pt-0"
                    hide-details
                  ></v-checkbox>
                </v-col>
                <v-col>
                  <span class="text-sm text-gray-600">
                    {{
                      unifiedTemplate
                        ? '(Same content will be used for WhatsApp, Email, and SMS)'
                        : '(Create different content for each channel independently)'
                    }}
                  </span>
                </v-col>
              </v-row>
            </div>

            <!-- Parameters Section -->
            <div class="mb-6" v-if="availableParamsList.length">
              <h3 class="text-md font-medium mb-3">
                Available Parameters (Associated with selected event)
              </h3>
              <div class="flex flex-wrap gap-2">
                <v-chip
                  v-for="variable in availableParamsList"
                  :key="variable.value"
                  @click="insertVariable(variable.value)"
                  class="cursor-pointer bg-blue-100 text-blue-700 font-medium hover:bg-blue-200"
                  size="small"
                  label
                >
                  <v-icon left x-small>mdi-plus</v-icon>
                  {{ variable.label }}
                </v-chip>
              </div>
            </div>

            <!-- Template Content Grid -->
            <div class="template-grid">
              <v-row>
                <!-- WhatsApp Column -->
                <v-col cols="12" md="4">
                  <div class="template-column">
                    <div class="column-header">
                      <v-icon color="green" class="mr-2">mdi-whatsapp</v-icon>
                      <span class="font-semibold">WhatsApp Template</span>
                    </div>

                    <!-- WhatsApp Letter Attachment -->
                    <div class="mb-4">
                      <v-checkbox
                        v-model="templates.whatsapp.attachLetter"
                        label="Attach PDF Letter"
                        dense
                        class="mt-0 pt-0"
                        hide-details
                        color="primary"
                      ></v-checkbox>
                      <div v-if="templates.whatsapp.attachLetter" class="mt-2">
                        <v-select
                          v-model="templates.whatsapp.letterTemplateId"
                          :items="letterTemplates"
                          item-text="name"
                          item-value="_id"
                          label="Select Letter"
                          outlined
                          dense
                          hide-details
                        ></v-select>
                      </div>
                    </div>

                    <!-- WhatsApp Message Body -->
                    <div class="mb-4">
                      <label class="text-sm font-medium text-gray-700 mb-2 block">
                        Message Body* <span class="text-xs text-gray-500">(max 1024 chars)</span>
                      </label>
                      <v-textarea
                        v-model="templates.whatsapp.messageBody"
                        outlined
                        auto-grow
                        rows="4"
                        placeholder="Enter WhatsApp message..."
                        :counter="1024"
                        :error-messages="errors.whatsapp.messageBody"
                        @input="onWhatsAppBodyChange"
                        dense
                      ></v-textarea>
                    </div>

                    <!-- WhatsApp Buttons -->
                    <div class="mb-4">
                      <div class="d-flex justify-space-between align-center mb-2">
                        <label class="text-sm font-medium text-gray-700">
                          Buttons <span class="text-xs text-gray-500">(Max 3)</span>
                        </label>
                        <v-btn
                          text
                          color="success"
                          x-small
                          @click="addWhatsAppButton"
                          :disabled="templates.whatsapp.buttons.length >= 3"
                        >
                          <v-icon small>mdi-plus</v-icon>
                          Add
                        </v-btn>
                      </div>

                      <div
                        v-for="(button, index) in templates.whatsapp.buttons"
                        :key="index"
                        class="mb-3"
                      >
                        <v-card outlined class="pa-3">
                          <div class="d-flex justify-space-between align-center mb-2">
                            <span class="text-xs font-medium">Button {{ index + 1 }}</span>
                            <v-btn icon x-small @click="removeWhatsAppButton(index)">
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>

                          <v-select
                            v-model="button.type"
                            :items="buttonTypes"
                            label="Type"
                            dense
                            outlined
                            class="mb-2"
                          ></v-select>

                          <v-text-field
                            v-model="button.text"
                            label="Button Text"
                            dense
                            outlined
                            :counter="25"
                            class="mb-2"
                          ></v-text-field>

                          <v-text-field
                            v-if="button.type === 'url'"
                            v-model="button.url"
                            label="URL"
                            dense
                            outlined
                            placeholder="https://example.com"
                          ></v-text-field>

                          <v-text-field
                            v-if="button.type === 'call'"
                            v-model="button.phone"
                            label="Phone Number"
                            dense
                            outlined
                            placeholder="+1234567890"
                          ></v-text-field>
                        </v-card>
                      </div>
                    </div>

                    <!-- WhatsApp Preview -->
                    <div class="preview-section">
                      <h4 class="text-sm font-medium mb-2">Preview</h4>
                      <div class="whatsapp-preview">
                        <div class="whatsapp-bg p-3 rounded-lg">
                          <!-- PDF Attachment Preview -->
                          <div
                            v-if="templates.whatsapp.attachLetter && templates.whatsapp.letterTemplateId"
                            class="bg-white p-2 rounded-lg mb-2 d-flex align-center border-l-4 border-blue-500 shadow-sm"
                          >
                            <v-icon color="primary" class="mr-2" size="20">mdi-file-pdf-box</v-icon>
                            <div class="truncate text-xs font-bold text-gray-700">
                              {{ getLetterName(templates.whatsapp.letterTemplateId) }}.pdf
                            </div>
                          </div>
                          <div class="bg-white p-3 rounded-lg mb-2">
                            <div
                              class="text-gray-800 text-sm"
                              v-html="getFormattedPreview('whatsapp')"
                            ></div>
                          </div>

                          <div
                            v-for="(button, index) in templates.whatsapp.buttons"
                            :key="index"
                            class="bg-white p-2 rounded-lg mb-1 text-center"
                          >
                            <span
                              class="text-sm font-medium"
                              :class="getButtonColorClass(button.type)"
                            >
                              <v-icon small :class="getButtonColorClass(button.type)">{{
                                getButtonIcon(button.type)
                              }}</v-icon>
                              {{ button.text || 'Button Text' }}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- Email Column -->
                <v-col cols="12" md="4">
                  <div class="template-column">
                    <div class="column-header">
                      <v-icon color="red" class="mr-2">mdi-email</v-icon>
                      <span class="font-semibold">Email Template</span>
                    </div>

                    <!-- Email Subject -->
                    <div class="mb-4">
                      <label class="text-sm font-medium text-gray-700 mb-2 block">
                        Subject* <span class="text-xs text-gray-500">(max 100 chars)</span>
                      </label>
                      <v-text-field
                        v-model="templates.email.subject"
                        outlined
                        dense
                        placeholder="Enter email subject..."
                        :counter="100"
                        :error-messages="errors.email.subject"
                        @input="onEmailSubjectChange"
                      ></v-text-field>
                    </div>

                    <!-- Email Letter Attachment -->
                    <div class="mb-4">
                      <v-checkbox
                        v-model="templates.email.attachLetter"
                        label="Attach PDF Letter"
                        dense
                        class="mt-0 pt-0"
                        hide-details
                        color="primary"
                      ></v-checkbox>
                      <div v-if="templates.email.attachLetter" class="mt-2">
                        <v-select
                          v-model="templates.email.letterTemplateId"
                          :items="letterTemplates"
                          item-text="name"
                          item-value="_id"
                          label="Select Letter"
                          outlined
                          dense
                          hide-details
                        ></v-select>
                      </div>
                    </div>

                    <!-- Email Message Body -->
                    <div class="mb-4">
                      <label class="text-sm font-medium text-gray-700 mb-2 block">
                        Message Body* <span class="text-xs text-gray-500">(max 2048 chars)</span>
                      </label>
                      <v-textarea
                        v-model="templates.email.messageBody"
                        outlined
                        auto-grow
                        rows="6"
                        placeholder="Enter email message..."
                        :counter="2048"
                        :error-messages="errors.email.messageBody"
                        @input="onEmailBodyChange"
                        dense
                      ></v-textarea>
                    </div>

                    <!-- Email Preview -->
                    <div class="preview-section">
                      <h4 class="text-sm font-medium mb-2">Preview</h4>
                      <div class="email-preview">
                        <div class="border rounded-lg p-3 bg-white">
                          <div class="border-b pb-2 mb-3">
                            <div class="text-xs text-gray-500 mb-1">Subject:</div>
                            <div class="font-medium text-sm">
                              {{ templates.email.subject || 'Email Subject' }}
                            </div>
                          </div>
                          <div
                            class="text-sm text-gray-800"
                            v-html="getFormattedPreview('email')"
                          ></div>

                          <!-- Email Attachment Preview -->
                          <div
                            v-if="templates.email.attachLetter && templates.email.letterTemplateId"
                            class="mt-4 pt-3 border-t border-gray-100"
                          >
                            <div class="d-flex align-center bg-gray-50 p-2 rounded-lg border border-dashed border-gray-300">
                              <v-icon color="grey darken-1" size="20" class="mr-2">mdi-attachment</v-icon>
                              <span class="text-xs font-bold text-gray-600">
                                {{ getLetterName(templates.email.letterTemplateId) }}.pdf
                              </span>
                              <v-spacer></v-spacer>
                              <span class="text-xxs text-gray-400">PDF Attachment</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>

                <!-- SMS Column -->
                <v-col cols="12" md="4">
                  <div class="template-column">
                    <div class="column-header">
                      <v-icon color="blue" class="mr-2">mdi-message-text</v-icon>
                      <span class="font-semibold">SMS Template</span>
                    </div>

                    <!-- SMS Message Body -->
                    <div class="mb-4">
                      <label class="text-sm font-medium text-gray-700 mb-2 block">
                        Message Body* <span class="text-xs text-gray-500">(max 160 chars)</span>
                      </label>
                      <v-textarea
                        v-model="templates.sms.messageBody"
                        outlined
                        auto-grow
                        rows="4"
                        placeholder="Enter SMS message..."
                        :counter="160"
                        :error-messages="errors.sms.messageBody"
                        @input="onSmsBodyChange"
                        dense
                      ></v-textarea>
                    </div>

                    <!-- SMS Preview -->
                    <div class="preview-section">
                      <h4 class="text-sm font-medium mb-2">Preview</h4>
                      <div class="sms-preview">
                        <div class="border rounded-lg p-3 bg-gray-50">
                          <div class="text-xs text-gray-500 mb-2">SMS Message</div>
                          <div
                            class="text-sm text-gray-800"
                            v-html="getFormattedPreview('sms')"
                          ></div>
                          <div class="text-xs text-gray-500 mt-2">
                            Characters: {{ (templates.sms.messageBody || '').length }}/160
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </div>
          </v-card-text>

          <!-- Footer -->
          <div class="d-flex justify-end p-4 border-t border-gray-200 flex-shrink-0">
            <v-btn class="mr-2 normal-case" outlined color="primary" @click="closeDialog" small>
              Cancel
            </v-btn>
            <v-btn
              color="primary"
              class="normal-case"
              @click="submitTemplate"
              small
              :loading="submitTemplateLoading"
            >
              <v-icon left>mdi-content-save</v-icon>
              <!-- {{ editingTemplate ? 'Update Template' : 'Submit for Approval' }} -->
              Save
            </v-btn>
          </div>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import { APP_URL, getNotificationTriggerOptions } from '@/utils/workforce/constants'

export default {
  name: 'WhatsAppTemplateCreator',
  components: {},
  props: {
    templateTriggersList: {
      type: Object,
      default: () => ({})
    },
    allTriggers: {
      type: Array,
      default: () => []
    },
    editingTemplate: {
      type: Object,
      default: null
    }
  },
  watch: {
    editingTemplate: {
      handler(template) {
        if (template) {
          this.templateName = template.templateName || ''
          this.category = template.category || ''
          this.language = template.whatsappLangCode || 'en'
          this.selectedEventType = template.eventType || null

          // Process message body and replace numbered params
          let messageBody = template.templateContent || ''
          if (template.params && template.params.length) {
            template.params.forEach((param, index) => {
              messageBody = messageBody.replace(`{{${index + 1}}}`, `{{${param}}}`)
            })
          }

          // Set unified template content
          this.templates.whatsapp.messageBody = messageBody
          this.templates.email.messageBody = messageBody
          this.templates.email.subject = template.templateSubject || ''
          this.templates.sms.messageBody = messageBody

          // Parse buttons if they exist
          if (template.buttons && template.buttons.length) {
            this.templates.whatsapp.buttons = template.buttons.map((btn) => {
              const buttonData = {
                type: this.mapButtonType(btn.type),
                text: btn.text || ''
              }

              if (buttonData.type === 'url') {
                buttonData.url = btn.url || ''
                buttonData.urlType = btn.dynamicType ? 'dynamic' : 'static'
                buttonData.dynamicType = btn.dynamicType || ''
              } else if (buttonData.type === 'call') {
                buttonData.phone = btn.phone || ''
              }

              return buttonData
            })
          }

          // Set letter content
          this.templates.whatsapp.attachLetter = !!template.whatsappLetterTemplateId
          this.templates.whatsapp.letterTemplateId = template.whatsappLetterTemplateId || null
          this.templates.email.attachLetter = !!template.emailLetterTemplateId
          this.templates.email.letterTemplateId = template.emailLetterTemplateId || null
          
          if (this.templates.whatsapp.attachLetter || this.templates.email.attachLetter) {
            this.fetchLetterTemplates()
          }
        } else {
          // Reset form when editingTemplate is null (switching to add mode)
          this.resetForm()
        }
      },
      immediate: true
    },
    unifiedTemplate: {
      handler(isUnified) {
        if (isUnified) {
          this.syncTemplates()
        }
      }
    }
  },
  data() {
    return {
      orgData: this.$storage.getUniversal('orgData'),
      selectedEventType: null,
      templateName: '',
      category: 'UTILITY',
      language: 'en',
      submitTemplateLoading: false,
      unifiedTemplate: true, // Auto-checked by default
      letterTemplates: [],
      fetchingLetters: false,

      // Template data structure
      templates: {
        whatsapp: {
          messageBody: '',
          buttons: [],
          attachLetter: false,
          letterTemplateId: null
        },
        email: {
          subject: '',
          messageBody: '',
          attachLetter: false,
          letterTemplateId: null
        },
        sms: {
          messageBody: ''
        }
      },

      // Form errors
      errors: {
        templateName: '',
        category: '',
        whatsapp: {
          messageBody: ''
        },
        email: {
          subject: '',
          messageBody: ''
        },
        sms: {
          messageBody: ''
        }
      },
      categories: [
        { text: 'Select a category', value: '' },
        { text: 'Utility', value: 'UTILITY' },
        { text: 'Marketing', value: 'MARKETING' },
        { text: 'Authentication', value: 'AUTHENTICATION' },
        { text: 'Task', value: 'task' }
      ],
      languages: [
        { text: 'English', value: 'en' },
        { text: 'Spanish', value: 'es' },
        { text: 'French', value: 'fr' },
        { text: 'German', value: 'de' },
        { text: 'Portuguese', value: 'pt' }
      ],
      buttonTypes: [
        { text: 'URL', value: 'url' },
        { text: 'Call', value: 'call' },
        { text: 'Quick Reply', value: 'quick_reply' }
      ],
      urlTypes: [
        { text: 'Static URL', value: 'static' },
        { text: 'Dynamic URL', value: 'dynamic' }
      ],
      dyanamicUrlTypes: [
        { text: 'Customer Feedback URL', value: 'customer_feedback_url' },
        { text: 'Customer Detail URL', value: 'customer_details_url' }
      ],
      typeOfNotificationOptions: [
        { text: 'Whatsapp', value: 'WHATSAPP' },
        { text: 'Email', value: 'EMAIL' },
        { text: 'Sms', value: 'SMS' }
      ],
      typesOfNotification: []
    }
  },
  computed: {
    availableParamsList() {
      if (!this.selectedEventType) return []

      // Search through all categories to find the selected trigger
      for (const categoryName in this.templateTriggersList) {
        const categoryTriggers = this.templateTriggersList[categoryName]
        if (categoryTriggers[this.selectedEventType]) {
          const params = categoryTriggers[this.selectedEventType].params || []
          return params
        }
      }

      return []
    },
    notificationTriggerOptions() {
      return getNotificationTriggerOptions()
    }
  },

  async mounted() {
    this.fetchLetterTemplates()
  },

  methods: {
    async fetchLetterTemplates() {
      try {
        this.fetchingLetters = true
        // Primary: API
        try {
          const response = await this.$axios.get('/letter-templates', {
            params: { limit: '*' }
          })
          const resData = response.data?.data || response.data || []
          this.letterTemplates = Array.isArray(resData) ? resData : []
        } catch (apiError) {
          console.warn('API fetch failed in AddTemplate, trying localStorage')
        }

        // Secondary: LocalStorage
        const stored = localStorage.getItem('finfi_letter_templates')
        let localLetters = stored ? JSON.parse(stored) : []
        
        // Dummy templates fallback
        if (this.letterTemplates.length === 0 && localLetters.length === 0) {
          const dummy = [
            { _id: 'dummy1', name: 'Standard Offer Letter' },
            { _id: 'dummy2', name: 'Loan Disbursement Notice' }
          ]
          localLetters = [...dummy]
        }

        // Merge
        const combined = [...this.letterTemplates]
        localLetters.forEach(ll => {
          if (!combined.find(cl => cl._id === ll._id)) {
            combined.push(ll)
          }
        })
        
        this.letterTemplates = combined
      } catch (error) {
        console.error('Error fetching letter templates:', error)
      } finally {
        this.fetchingLetters = false
      }
    },
    getLetterName(id) {
      const letter = this.letterTemplates.find((l) => l._id === id)
      return letter ? letter.name : 'Document'
    },
    mapButtonType(type) {
      const typeMap = {
        URL: 'url',
        PHONE_NUMBER: 'call',
        QUICK_REPLY: 'quick_reply'
      }
      return typeMap[type] || 'url'
    },

    formatTemplateName() {
      this.templateName = this.templateName.replace(/\s+/g, '_').toLowerCase()
      this.templateName = this.templateName.replace(/[^a-z0-9_]/g, '')
      this.validateTemplateName()
    },

    validateTemplateName() {
      if (!this.templateName) {
        this.errors.templateName = 'Template name is required'
        return false
      } else if (this.templateName.length > 512) {
        this.errors.templateName = 'Template name must be 512 characters or less'
        return false
      } else if (!/^[a-z0-9_]+$/.test(this.templateName)) {
        this.errors.templateName = 'Only lowercase letters, numbers and underscores are allowed'
        return false
      } else {
        this.errors.templateName = ''
        return true
      }
    },

    validateCategory() {
      if (!this.category) {
        this.errors.category = 'Category is required'
        return false
      } else {
        this.errors.category = ''
        return true
      }
    },

    syncTemplates() {
      if (this.unifiedTemplate) {
        const whatsappBody = this.templates.whatsapp.messageBody
        this.templates.email.messageBody = whatsappBody
        this.templates.sms.messageBody = whatsappBody

        if (this.templates.sms.messageBody.length > 160) {
          this.templates.sms.messageBody = this.templates.sms.messageBody.substring(0, 160)
        }
      }
    },

    onWhatsAppBodyChange() {
      this.validateWhatsAppBody()
      if (this.unifiedTemplate) {
        this.syncTemplates()
      }
    },

    validateWhatsAppBody() {
      if (!this.templates.whatsapp.messageBody) {
        this.errors.whatsapp.messageBody = 'WhatsApp message body is required'
        return false
      } else if (this.templates.whatsapp.messageBody.length > 1024) {
        this.errors.whatsapp.messageBody = 'WhatsApp message body must be 1024 characters or less'
        return false
      } else {
        this.errors.whatsapp.messageBody = ''
        return true
      }
    },

    addWhatsAppButton() {
      if (this.templates.whatsapp.buttons.length >= 3) return

      this.templates.whatsapp.buttons.push({
        type: 'url',
        text: '',
        url: '',
        urlType: 'static',
        phone: ''
      })
    },

    removeWhatsAppButton(index) {
      this.templates.whatsapp.buttons.splice(index, 1)
    },

    onEmailSubjectChange() {
      this.validateEmailSubject()
    },

    onEmailBodyChange() {
      this.validateEmailBody()
      if (this.unifiedTemplate) {
        this.templates.whatsapp.messageBody = this.templates.email.messageBody
        this.templates.sms.messageBody = this.templates.email.messageBody.substring(0, 160)
      }
    },

    validateEmailSubject() {
      if (!this.templates.email.subject) {
        this.errors.email.subject = 'Email subject is required'
        return false
      } else if (this.templates.email.subject.length > 100) {
        this.errors.email.subject = 'Email subject must be 100 characters or less'
        return false
      } else {
        this.errors.email.subject = ''
        return true
      }
    },

    validateEmailBody() {
      if (!this.templates.email.messageBody) {
        this.errors.email.messageBody = 'Email message body is required'
        return false
      } else if (this.templates.email.messageBody.length > 2048) {
        this.errors.email.messageBody = 'Email message body must be 2048 characters or less'
        return false
      } else {
        this.errors.email.messageBody = ''
        return true
      }
    },

    onSmsBodyChange() {
      this.validateSmsBody()
      if (this.unifiedTemplate) {
        this.templates.whatsapp.messageBody = this.templates.sms.messageBody
        this.templates.email.messageBody = this.templates.sms.messageBody
      }
    },

    validateSmsBody() {
      if (!this.templates.sms.messageBody) {
        this.errors.sms.messageBody = 'SMS message body is required'
        return false
      } else if (this.templates.sms.messageBody.length > 160) {
        this.errors.sms.messageBody = 'SMS message body must be 160 characters or less'
        return false
      } else {
        this.errors.sms.messageBody = ''
        return true
      }
    },

    getFormattedPreview(type) {
      let content = ''
      switch (type) {
        case 'whatsapp':
          content = this.templates.whatsapp.messageBody
          break
        case 'email':
          content = this.templates.email.messageBody
          break
        case 'sms':
          content = this.templates.sms.messageBody
          break
      }

      if (!content) return '[Message body will appear here]'

      return content.replace(
        /\{\{([^}]+)\}\}/g,
        '<span class="variable bg-green-100 text-green-600 rounded px-1 font-medium">{{$1}}</span>'
      )
    },

    getButtonColorClass(type) {
      switch (type) {
        case 'url':
          return 'text-blue-400'
        case 'call':
          return 'text-green-600'
        case 'quick_reply':
          return 'text-gray-700'
        default:
          return 'text-gray-700'
      }
    },

    getButtonIcon(type) {
      switch (type) {
        case 'url':
          return 'mdi-open-in-new'
        case 'call':
          return 'mdi-phone'
        case 'quick_reply':
          return 'mdi-reply'
        default:
          return 'mdi-open-in-new'
      }
    },

    insertVariable(variable) {
      const variableText = `{{${variable}}}`

      if (this.unifiedTemplate) {
        this.templates.whatsapp.messageBody += variableText
        this.templates.email.messageBody += variableText
        this.templates.sms.messageBody += variableText

        if (this.templates.sms.messageBody.length > 160) {
          this.templates.sms.messageBody = this.templates.sms.messageBody.substring(0, 160)
        }
      } else {
        this.templates.whatsapp.messageBody += variableText
      }

      this.validateWhatsAppBody()
      this.validateEmailBody()
      this.validateSmsBody()
    },

    validateForm() {
      const isNameValid = this.validateTemplateName()
      const isCategoryValid = this.validateCategory()
      const isWhatsAppValid = this.validateWhatsAppBody()
      const isEmailSubjectValid = this.validateEmailSubject()
      const isEmailBodyValid = this.validateEmailBody()
      const isSmsValid = this.validateSmsBody()

      return (
        isNameValid &&
        isCategoryValid &&
        isWhatsAppValid &&
        isEmailSubjectValid &&
        isEmailBodyValid &&
        isSmsValid
      )
    },

    closeDialog() {
      this.$nextTick(() => {
        this.submitTemplateLoading = false
        this.resetForm()
        this.$emit('close')
      })
    },

    resetForm() {
      // Reset form data
      this.templateName = ''
      this.category = 'UTILITY'
      this.language = 'en'
      this.selectedEventType = null
      this.templates.whatsapp.messageBody = ''
      this.templates.email.subject = ''
      this.templates.email.messageBody = ''
      this.templates.sms.messageBody = ''
      this.templates.whatsapp.buttons = []
      this.templates.whatsapp.attachLetter = false
      this.templates.whatsapp.letterTemplateId = null
      this.templates.email.attachLetter = false
      this.templates.email.letterTemplateId = null
      this.errors = {
        templateName: '',
        category: '',
        whatsapp: {
          messageBody: ''
        },
        email: {
          subject: '',
          messageBody: ''
        },
        sms: {
          messageBody: ''
        }
      }
    },

    submitTemplate() {
      if (this.validateForm()) {
        this.submitTemplateLoading = true

        const variableRegex = /\{\{([^}]+)\}\}/g
        const matches = [...this.templates.whatsapp.messageBody.matchAll(variableRegex)]
        const params = matches.map((match) => match[1].trim())

        let formattedBody = this.templates.whatsapp.messageBody
        params.forEach((param, index) => {
          const regex = new RegExp(`\\{\\{${param}\\}\\}`, 'g')
          formattedBody = formattedBody.replace(regex, `{{${index + 1}}}`)
        })

        const payload = {
          templateName: this.templateName,
          templateContent: formattedBody,
          templateSubject: this.templates.email.subject,
          whatsappLangCode: this.language,
          category: this.category.toUpperCase(),
          typesOfNotification: ['WHATSAPP', 'EMAIL', 'SMS'],
          params: params,
          eventType: this.selectedEventType || undefined,
          emailContent: this.templates.email.messageBody,
          smsContent: this.templates.sms.messageBody,
          whatsappLetterTemplateId: this.templates.whatsapp.attachLetter
            ? this.templates.whatsapp.letterTemplateId
            : '',
          emailLetterTemplateId: this.templates.email.attachLetter ? this.templates.email.letterTemplateId : ''
        }

        payload.approvalStatus = ['WHATSAPP', 'EMAIL', 'SMS'].map((el) => {
          return { typeOfNotification: el, status: 'PENDING' }
        })

        payload.example = params.map((param) => `Sample ${param}`)

        if (this.templates.whatsapp.buttons.length > 0) {
          payload.buttons = this.templates.whatsapp.buttons.map((button) => {
            const buttonTypeMap = {
              quick_reply: 'QUICK_REPLY',
              url: 'URL',
              call: 'PHONE_NUMBER'
            }

            const buttonPayload = {
              type: buttonTypeMap[button.type] || 'URL',
              text: button.text
            }

            if (button.type === 'url') {
              buttonPayload.url = button.url
              buttonPayload.urlType = button.urlType

              if (button.urlType === 'dynamic') {
                buttonPayload.dynamicType = button.dynamicType
                buttonPayload.example = `https://vk.myfinfi.com/open/details/${this.orgData?.id}?cid=test123`
              }
            }

            if (button.type === 'call') {
              buttonPayload.phone = button.phone
            }

            return buttonPayload
          })
        }
        console.log({ payload })

        const method = this.editingTemplate ? 'put' : 'post'
        const endpoint = this.editingTemplate
          ? `/whatsapp/templates/${this.editingTemplate.whatsappTemplateId}`
          : '/whatsapp/templates'

        this.$nextTick(() => {
          this.$axios[method](endpoint, payload)
            .then(() => {
              this.$toast.success(
                `Template ${this.editingTemplate ? 'updated' : 'submitted'} successfully!`
              )
              this.submitTemplateLoading = false
              this.closeDialog()
              // Emit an event to refresh the templates list
              this.$emit('template-submitted')
            })
            .catch((error) => {
              console.error(
                `Error ${this.editingTemplate ? 'updating' : 'submitting'} template:`,
                error
              )
              this.submitTemplateLoading = false
              this.$toast.error(
                error.response?.data?.message ||
                  `Failed to ${
                    this.editingTemplate ? 'update' : 'submit'
                  } template. Please try again.`
              )
            })
        })
      } else {
        this.$nextTick(() => {
          this.$toast.error('Please fix the errors in the form before submitting.')
        })
      }
    }
  }
}
</script>

<style>
.template-container {
  height: 90vh;
  display: flex;
  flex-direction: column;
}

.template-grid {
  flex: 1;
}

.template-column {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 16px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.column-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.preview-section {
  margin-top: auto;
  padding-top: 16px;
}

.whatsapp-preview {
  min-height: 200px;
}

.email-preview {
  min-height: 200px;
}

.sms-preview {
  min-height: 150px;
}

.variable {
  background-color: #e5f7e9;
  color: #25d366;
  border-radius: 4px;
  padding: 2px 4px;
  font-weight: 500;
  cursor: pointer;
}

.top-header {
  position: sticky;
  top: 0;
  background-color: #fff;
  z-index: 500;
}
.form-title {
  font-size: 1.25rem;
  font-weight: 600;
  padding: 16px;
  color: #21427d;
  margin: 0;
}

.whatsapp-bg {
  background-color: #e5ded8;
  background-image: url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='rgba(0,0,0,0.05)' fill-rule='evenodd'/%3E%3C/svg%3E");
}
</style>
